import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import logo from '../assets/logo-asistelo.png';

export const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [superAdminCode, setSuperAdminCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('¡Bienvenido de nuevo!');
        navigate('/');
      } else {
        const response = await register(email, password, name, superAdminCode);
        
        // Verificar si requiere verificación de email
        if (response?.email_verification_required) {
          toast.info('Por favor verifica tu email para continuar');
          navigate('/email-verification-pending', { state: { email } });
        } else {
          toast.success('¡Cuenta creada exitosamente!');
          navigate('/');
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error en la autenticación';
      
      // Manejar error de email no verificado
      if (errorMsg.includes('Email no verificado') || errorMsg.includes('verifica tu email')) {
        toast.error('Email no verificado');
        navigate('/email-verification-pending', { state: { email } });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Color de fondo claro */}
      <div
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundColor: '#f8f9fa',
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-center items-center px-12">
          <div className="flex flex-col items-center">
            <img src={logo} alt="Asistelo" className="h-15 w-auto mb-6" />
            <p className="text-xl text-slate-700 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
              Sistema profesional de gestión de pólizas de seguros
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex flex-col items-center gap-3 mb-4 lg:hidden">
              <img src={logo} alt="Asistelo" className="h-16 w-auto" />
              <p className="text-sm text-slate-600 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                Sistema profesional de gestión de pólizas de seguros
              </p>
            </div>
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </h2>
            <p className="text-sm text-slate-600 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              {isLogin ? 'Accede a tu cuenta de Asístelo' : 'Regístrate para comenzar'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Nombre Completo
                </Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={!isLogin}
                  className="mt-1"
                  placeholder="Juan Pérez"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Correo Electrónico
              </Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Contraseña
                </Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-[#2537b0] hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="••••••••"
              />
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="superAdminCode" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Código Super Admin <span className="text-slate-400 normal-case">(Opcional)</span>
                </Label>
                <Input
                  id="superAdminCode"
                  data-testid="super-admin-code-input"
                  type="text"
                  value={superAdminCode}
                  onChange={(e) => setSuperAdminCode(e.target.value)}
                  className="mt-1"
                  placeholder="Ingresa el código si eres Super Admin"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Deja este campo vacío para registrarte como Usuario Principal (Admin)
                </p>
              </div>
            )}

            <Button
              data-testid="submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#2537b0] hover:bg-[#1e2b8f] text-white font-medium py-3"
            >
              {loading ? 'Procesando...' : isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-mode-button"
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-[#2537b0] hover:underline"
            >
              {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
